import { claimMeasurementContainer } from './container-owner';

export const MARKETING_GTM_ID = 'GTM-TFWBCHTS';
export const MARKETING_MEASUREMENT_ID = 'G-7PT65WWXN9';
export const ANALYTICS_CONSENT_KEY = 'leagen-marketing-analytics-consent';
export const ANALYTICS_PREFERENCES_EVENT = 'leagen-analytics-preferences';
export type AnalyticsConsent = 'unknown' | 'accepted' | 'rejected';
export type MarketingLeadForm = 'contact' | 'beta';

type AnalyticsWindow = {
  dataLayer?: unknown[];
  [key: `ga-disable-${string}`]: boolean | undefined;
};

let active = false;
let initialized = false;
let lastPage: string | undefined;
let volatileConsent: AnalyticsConsent | undefined;
const receipts = new Set<string>();
const listeners = new Set<() => void>();

export function getAnalyticsConsent(): AnalyticsConsent {
  if (volatileConsent) return volatileConsent;
  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    // Earlier permission covered only the public website. Ask once for the
    // expanded website/app purpose, while preserving an earlier refusal.
    return value === 'accepted-v2'
      ? 'accepted'
      : value === 'rejected'
        ? 'rejected'
        : 'unknown';
  } catch {
    return 'unknown';
  }
}

export function subscribeAnalyticsConsent(listener: () => void) {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === ANALYTICS_CONSENT_KEY || event.key === null) {
      volatileConsent = undefined;
      if (getAnalyticsConsent() !== 'accepted') stopMarketingMeasurement();
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function setAnalyticsConsent(value: 'accepted' | 'rejected') {
  volatileConsent = value;
  try {
    window.localStorage.setItem(
      ANALYTICS_CONSENT_KEY,
      value === 'accepted' ? 'accepted-v2' : value,
    );
  } catch {
    // The choice still applies to this visit when storage is unavailable.
  }
  if (value === 'rejected') stopMarketingMeasurement();
  listeners.forEach((listener) => listener());
}

export function isMarketingOrigin(url: string, production: boolean) {
  try {
    const parsed = new URL(url);
    return (
      production &&
      parsed.protocol === 'https:' &&
      !parsed.port &&
      ['leagen.co', 'www.leagen.co'].includes(parsed.hostname)
    );
  } catch {
    return false;
  }
}

/** Only public marketing routes can become analytics locations. */
export function marketingPageLocation(href: string): string | null {
  try {
    const url = new URL(href);
    if (
      !/^\/(?:contact|axcelerate|faq|pricing|privacy-policy|terms-of-service|cookie-policy|dpa|auth\/sign-up|thank-you\/(?:contact|beta)|blog(?:\/[a-z0-9-]+)?)?\/?$/.test(
        url.pathname,
      )
    )
      return null;
    const clean = new URL(url.pathname, url.origin);
    const allowed: Record<string, readonly string[]> = {
      utm_source: [
        'google',
        'bing',
        'linkedin',
        'facebook',
        'instagram',
        'youtube',
        'x',
        'newsletter',
      ],
      utm_medium: [
        'organic',
        'organic-social',
        'social',
        'email',
        'cpc',
        'referral',
      ],
      utm_campaign: ['launch_2026', 'beta_2026', 'axday2026'],
    };
    for (const [key, values] of Object.entries(allowed)) {
      const value = url.searchParams.get(key)?.toLowerCase();
      if (value && values.includes(value)) clean.searchParams.set(key, value);
    }
    return clean.href;
  } catch {
    return null;
  }
}

function enqueue(..._args: unknown[]) {
  const browser = window as unknown as AnalyticsWindow;
  try {
    browser.dataLayer ??= [];
    // gtag consumes an Arguments object, not a dataLayer event array.
    // eslint-disable-next-line prefer-rest-params
    browser.dataLayer.push(arguments);
  } catch {
    // Measurement must never interrupt an enquiry or navigation.
  }
}

function pushTagManager(value: Record<string, unknown>) {
  try {
    const browser = window as unknown as AnalyticsWindow;
    browser.dataLayer ??= [];
    browser.dataLayer.push(value);
  } catch {
    // A blocked tag manager must never interrupt the website.
  }
}

function emitMarketingEvent(name: string, parameters: Record<string, unknown>) {
  pushTagManager({
    event: 'leagen_measurement',
    leagen_analytics_consent: 'granted',
    measurement_event: name,
    measurement: {
      form_name: null,
      form_step: null,
      content_type: null,
      item_id: null,
      ...parameters,
    },
  });
}

function referrerOrigin() {
  try {
    return new URL(document.referrer).origin + '/';
  } catch {
    return '';
  }
}

export function startMarketingMeasurement(production: boolean): boolean {
  if (
    typeof window === 'undefined' ||
    !isMarketingOrigin(window.location.href, production) ||
    !marketingPageLocation(window.location.href) ||
    getAnalyticsConsent() !== 'accepted'
  ) {
    if (
      typeof window !== 'undefined' &&
      !marketingPageLocation(window.location.href)
    )
      lastPage = undefined;
    return false;
  }

  if (!claimMeasurementContainer(MARKETING_GTM_ID)) return false;
  const browser = window as unknown as AnalyticsWindow;
  browser[`ga-disable-${MARKETING_MEASUREMENT_ID}`] = false;
  active = true;
  if (!initialized) {
    enqueue('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    enqueue('set', {
      page_title: 'Leagen public website',
      page_location: marketingPageLocation(window.location.href),
      page_referrer: referrerOrigin(),
    });
    pushTagManager({ 'gtm.start': Date.now(), event: 'gtm.js' });
    initialized = true;
  } else {
    enqueue('consent', 'update', { analytics_storage: 'granted' });
  }
  pushTagManager({ event: 'leagen_init', leagen_analytics_consent: 'granted' });
  return true;
}

export function stopMarketingMeasurement() {
  active = false;
  if (typeof window === 'undefined') return;
  if (!marketingPageLocation(window.location.href)) lastPage = undefined;
  (window as unknown as AnalyticsWindow)[
    `ga-disable-${MARKETING_MEASUREMENT_ID}`
  ] = true;
  if (initialized) {
    pushTagManager({ leagen_analytics_consent: 'denied' });
    enqueue('consent', 'update', { analytics_storage: 'denied' });
  }
  if (getAnalyticsConsent() === 'rejected') {
    for (const name of ['_ga', `_ga_${MARKETING_MEASUREMENT_ID.slice(2)}`]) {
      document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax; Secure`;
    }
  }
}

export function trackMarketingPage() {
  if (!active || getAnalyticsConsent() !== 'accepted') return;
  const page = marketingPageLocation(window.location.href);
  if (!page || page === lastPage) return;
  const context = {
    page_title: 'Leagen public website',
    page_location: page,
    page_referrer: lastPage ?? referrerOrigin(),
  };
  enqueue('set', context);
  emitMarketingEvent('page_view', {
    ...context,
  });
  lastPage = page;
}

/** Call only after a successful server response; the receipt never leaves the browser. */
export function trackMarketingLead(form: MarketingLeadForm, receipt: string) {
  if (!active || getAnalyticsConsent() !== 'accepted') return;
  const page = marketingPageLocation(window.location.href);
  const key = `${form}:${receipt}`;
  if (!page || receipts.has(key)) return;
  receipts.add(key);
  emitMarketingEvent('generate_lead', {
    form_name: form,
    page_title: 'Leagen public website',
    page_location: page,
    page_referrer: referrerOrigin(),
  });
}

export function trackMarketingCta(
  cta: 'contact' | 'beta' | 'pricing' | 'axcelerate',
) {
  if (!active || getAnalyticsConsent() !== 'accepted') return;
  const page = marketingPageLocation(window.location.href);
  if (!page) return;
  emitMarketingEvent('select_content', {
    content_type: 'website_cta',
    item_id: cta,
    page_location: page,
    page_title: 'Leagen public website',
    page_referrer: referrerOrigin(),
  });
}

/** Fixed funnel categories only; never read or send field values or error text. */
export function trackMarketingForm(
  form: MarketingLeadForm,
  event:
    | 'form_start'
    | 'form_progress'
    | 'form_submit_attempt'
    | 'form_submit_error',
  step: 1 | 2 | 3,
) {
  if (!active || getAnalyticsConsent() !== 'accepted') return;
  const page = marketingPageLocation(window.location.href);
  if (!page) return;
  emitMarketingEvent(event, {
    form_name: form,
    form_step: step,
    page_title: 'Leagen public website',
    page_location: page,
    page_referrer: referrerOrigin(),
  });
}
